import { useCallback, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import type { Number3 } from '../types';
import { useAbyssViewerContext } from './AbyssViewerContext';

type Props = {
  targetId: string;
  position?: Number3;
  rotation?: Number3;
  scale?: Number3;
  rotationCenter?: Number3;
  shouldDetectRotationCenter?: boolean;
  children: React.ReactNode;
};

const ALL_ZEROS: Number3 = [0, 0, 0];
const ALL_ONES: Number3 = [1, 1, 1];

export const TransformTargetGroup = ({
  targetId,
  position = ALL_ZEROS,
  rotation = ALL_ZEROS,
  scale = ALL_ONES,
  rotationCenter: rotationCenterProp,
  shouldDetectRotationCenter = false,
  children,
}: Props) => {
  const { addTransformTarget, removeTransformTarget } = useAbyssViewerContext();
  const [modelGroup, setModelGroup] = useState<THREE.Group>();
  const [leafGroup, setLeafGroup] = useState<THREE.Group>();

  const handleModelGroup = useCallback((group: THREE.Group) => {
    setModelGroup(group ?? undefined);
  }, []);

  const handleLeafGroup = useCallback((group: THREE.Group) => {
    setLeafGroup(group ?? undefined);
  }, []);

  useEffect(() => {
    if (modelGroup && leafGroup) {
      addTransformTarget(targetId, {
        targetGroup: modelGroup,
        leafGroup: leafGroup,
      });
      return () => {
        removeTransformTarget(targetId);
      };
    }
    return () => {};
  }, [targetId, modelGroup, leafGroup, addTransformTarget, removeTransformTarget]);

  const rotationCenter: Number3 | undefined = useMemo(() => {
    if (rotationCenterProp) {
      return rotationCenterProp;
    }
    if (!leafGroup || !shouldDetectRotationCenter) {
      return undefined;
    }
    const bbox = new THREE.Box3();
    bbox.setFromObject(leafGroup);
    const center = new THREE.Vector3();
    bbox.getCenter(center);
    // const localCenter = leafGroup.worldToLocal(center);
    // return [localCenter.x, localCenter.y, localCenter.z];
    return [center.x, center.y, center.z];
  }, [leafGroup, rotationCenterProp, shouldDetectRotationCenter]);

  const negativeRotationCenter: Number3 | undefined = useMemo(() => {
    if (!rotationCenter) return undefined;
    return [-rotationCenter[0], -rotationCenter[1], -rotationCenter[2]];
  }, [rotationCenter]);

  const pose = useMemo(() => {
    // Adjust actual pose according to non-zero rotation center
    if (!rotationCenter || !negativeRotationCenter) {
      return { position, rotation, scale };
    }
    const poseMat4 = new THREE.Matrix4().compose(
      new THREE.Vector3(...position),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
      new THREE.Vector3(...scale)
    );
    // Trc - rotation center translation, Tnegrc - negative rotation center translation
    // poseMat4 = Trc * adjMat4 * Tnegrc  (these correspond to groups tree below)
    // inv(Trc) * poseMat4 * inv(Tnegrc) = adjMat4
    // assuming Tnegrc = inv(Trc)
    // Tnegrc * poseMat4 * Trc = adjMat4
    const rcMat = new THREE.Matrix4().makeTranslation(new THREE.Vector3(...rotationCenter));
    const negrcMat = new THREE.Matrix4().makeTranslation(
      new THREE.Vector3(...negativeRotationCenter)
    );
    const adjMat4 = poseMat4.premultiply(negrcMat).multiply(rcMat);
    const positionVec = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scaleVec = new THREE.Vector3();
    adjMat4.decompose(positionVec, quat, scaleVec);
    const euler = new THREE.Euler().setFromQuaternion(quat);

    return { position: positionVec, rotation: euler, scale: scaleVec };
  }, [position, rotation, scale, rotationCenter, negativeRotationCenter]);

  return (
    // adjust center of rotation
    <group position={rotationCenter}>
      <group ref={handleModelGroup} {...pose}>
        {/* target object shifted to have 0,0,0 local center, it's a rotation point for the transform controls */}
        <group position={negativeRotationCenter}>
          <group ref={handleLeafGroup}>{children}</group>
        </group>
      </group>
    </group>
  );
};
